import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { Header } from '@components/Header';
import { PagePagination } from '@components/core-ui/Pagination';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { JobStatusText } from '@components/JobStatusText';
import { downloadCSV } from '@helpers/downloadCSV';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { jobIsTranscribing } from '@helpers/jobStatus/jobIsTranscribing';
import { secondsToHHMMSS } from '@helpers/secondsToHHMMSS';
import { toUnixSecondsQueryParam } from '@helpers/toUnixSecondsQueryParam';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ReportsService } from 'app/API';
import { JOB_STATUSES_ENUM } from 'app/types/enums/jobStatuses';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { TJobsFilterUpdate, useJobsFilterQueryParams } from 'app/hooks/useJobsFilterQueryParams';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { reportsKeys, TJobsReportListParams } from 'app/queries/reports/reportsKeys';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { buildJobsReportRequest } from './api/buildJobsReportRequest';
import { createFetchJobsReportQueryFn } from './api/fetchJobsReport';
import { buildJobsReportCsvRows, JOBS_REPORT_CSV_HEADERS } from './helpers/buildJobsReportCsvRows';
import { CellContent } from './styles/CellContent';
import { CenteredPercents } from './styles/CenteredPercents';
import { Content } from './styles/Content';
import { StyledCircularProgress } from './styles/StyledCircularProgress';
import { StyledProgressBarTextWrapper } from './styles/StyledProgressBarTextWrapper';
import { StyledProgressBarWrapper } from './styles/StyledProgressBarWrapper';

const SEARCH_DEBOUNCE_MS = 300;

export const JobsReport = () => {
  const { call } = useAPI();
  const { openModal } = useModalContext();
  const { searchParams, page, pageSize, search, setSearch, setPage, setPageSize, updateParams } = useListQueryParams({
    defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
  });
  const { jobsFilter, applyJobsFilter } = useJobsFilterQueryParams({ searchParams, updateParams });
  const { workspace, organization } = useAuthContext();
  const workspaceId = workspace?.id ?? '';

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const jobsFrom = toUnixSecondsQueryParam(jobsFilter.from);
  const jobsTo = toUnixSecondsQueryParam(jobsFilter.to);
  const listParams = useMemo<TJobsReportListParams>(
    () => ({ page, pageSize, search: debouncedSearch, jobsFrom, jobsTo }),
    [debouncedSearch, jobsFrom, jobsTo, page, pageSize],
  );

  const reportsQuery = useQuery({
    queryKey: reportsKeys.workspaceJobsList(workspaceId, listParams),
    queryFn: createFetchJobsReportQueryFn(call),
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[0].workspaceId === workspaceId ? previousData : undefined,
    enabled: workspaceId !== '' && !isSearchSettling,
  });
  const jobs = reportsQuery.data?.data ?? [];
  const jobsCount = reportsQuery.data?.count ?? 0;

  const exportMutation = useMutation({
    mutationFn: () =>
      call(() =>
        ReportsService.getJobsReport(
          buildJobsReportRequest(workspaceId, { ...listParams, page: 1, pageSize: Math.max(jobsCount, 1) }),
        ),
      ),
    onSuccess: (response) => {
      downloadCSV(
        buildJobsReportCsvRows(response.data, {
          organizationName: organization?.name ?? '',
          workspaceName: workspace?.name ?? '',
        }),
        JOBS_REPORT_CSV_HEADERS,
      );
    },
    onError: () => toast.error('Failed to export job reports'),
  });

  const openFiltersModal = () => {
    openModal(ModalNamesEnum.JobsReportFilters, {
      jobsFilter,
      jobsFilterOnChange: (update: TJobsFilterUpdate) =>
        applyJobsFilter(update, { onUnchanged: () => void reportsQuery.refetch() }),
      onSubmit: () => undefined,
    });
  };

  const isLoading = reportsQuery.isFetching || isSearchSettling || exportMutation.isPending;
  const isExportDisabled =
    isSearchSettling || reportsQuery.isPlaceholderData || !reportsQuery.isSuccess || exportMutation.isPending;

  return (
    <>
      <Helmet>
        <title>Job Reports - AutoScript</title>
      </Helmet>
      <Header
        title="Job Reports"
        setSearch={setSearch}
        search={search}
        buttonLabel={jobs.length ? 'Export CSV' : undefined}
        buttonOnClick={jobs.length ? () => exportMutation.mutate() : undefined}
        buttonDisabled={isExportDisabled}
        buttonLoading={exportMutation.isPending}
        onFilterClick={openFiltersModal}
        filterCounter={jobsFilter.from || jobsFilter.to ? 1 : 0}
      />

      <LinearProgressLoader active={isLoading} />
      <Content>
        {reportsQuery.isError ? (
          <div className="mb-4 flex items-center gap-3 text-xs text-[var(--color-text-muted)]" role="alert">
            <span>Failed to load job reports.</span>
            <TextButton type="button" onClick={() => void reportsQuery.refetch()}>
              Retry
            </TextButton>
          </div>
        ) : null}
        <TableWrapper>
          <Table>
            <Thead>
              <Tr>
                <Th>Creator</Th>
                <Th>Job Title</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th>Asr used</Th>
                <Th style={{ width: '1%' }}>Edits %</Th>
              </Tr>
            </Thead>
            <Tbody>
              {jobs.map((data) => {
                const percent = Math.round(data.editingProgress || 0);
                return (
                  <Tr key={data.id}>
                    <Td>
                      <CellContent>
                        <span style={{ display: 'block' }}>
                          {data.name} {data.lastname}
                        </span>
                        <span style={{ display: 'block' }}>{data.email}</span>
                      </CellContent>
                    </Td>
                    <Td>
                      <CellContent className="max-w-full text-ellipsis overflow-hidden">
                        {data.jobName || 'Unnamed'}
                      </CellContent>
                    </Td>
                    <Td>
                      <CellContent>
                        <JobStatusText status={data.status} removed={data.removed} />
                      </CellContent>
                    </Td>
                    <Td>
                      <CellContent>{getAmericanTime(data.createdAt)}</CellContent>
                    </Td>
                    <Td>
                      <CellContent>{secondsToHHMMSS(data.duration ?? 0)}</CellContent>
                    </Td>
                    <Td>
                      <CenteredPercents>
                        {jobIsTranscribing(data.status as keyof typeof JOB_STATUSES_ENUM) ? (
                          <StyledProgressBarWrapper>
                            <StyledCircularProgress
                              size={20}
                              trackWidth={3}
                              indicatorWidth={3}
                              progress={percent}
                              trackColor="var(--color-tint)"
                              indicatorColor="var(--color-text-muted)"
                            />
                            <StyledProgressBarTextWrapper>{percent}%</StyledProgressBarTextWrapper>
                          </StyledProgressBarWrapper>
                        ) : null}
                      </CenteredPercents>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableWrapper>
        <PagePagination
          totalItemsCount={jobsCount}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </Content>
    </>
  );
};
